import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import PageHero from '../components/common/PageHero';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { getPasswordError } from '../lib/utils';
import { Lock, AlertTriangle } from 'lucide-react';

const ChangePassword = () => {
  const { changePassword, logout, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const mustChange = Boolean(user?.must_change_password);
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.oldPassword || !formData.newPassword || !formData.confirmPassword) {
      toast({
        title: 'Missing fields',
        description: 'Please fill in all password fields.',
        variant: 'destructive'
      });
      return;
    }

    const passwordError = getPasswordError(formData.newPassword);
    if (passwordError) {
      toast({
        title: 'Invalid password',
        description: passwordError,
        variant: 'destructive'
      });
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast({
        title: 'Passwords do not match',
        description: 'Please make sure your new passwords match.',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await changePassword(formData.oldPassword, formData.newPassword);
      
      if (result.success) {
        toast({
          title: 'Password changed',
          description: 'Your password has been changed successfully.'
        });
        
        // Clear form
        setFormData({
          oldPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        
        // Redirect to profile after 2 seconds
        setTimeout(() => {
          navigate('/profile');
        }, 2000);
      } else {
        toast({
          title: 'Password change failed',
          description: result.error || 'Failed to change password.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHero title="CHANGE PASSWORD" />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {mustChange && (
            <div className="mb-6 flex items-start gap-3 rounded-lg border border-yellow-300 bg-yellow-50 p-4">
              <AlertTriangle size={20} className="text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-yellow-800">Password change required</p>
                <p className="text-sm text-yellow-700">
                  Your account is using a temporary password. Set a new password to continue using member features.
                </p>
              </div>
            </div>
          )}
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Change Your Password</CardTitle>
              <p className="text-sm text-gray-600 mt-2">
                Enter your current password and choose a new secure password
                (min 8 characters, uppercase, lowercase, number, special character).
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="oldPassword">
                    Current Password <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="oldPassword"
                    name="oldPassword"
                    type="password"
                    value={formData.oldPassword}
                    onChange={handleChange}
                    placeholder="Enter your current password"
                    className="mt-2"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="newPassword">
                    New Password <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    value={formData.newPassword}
                    onChange={handleChange}
                    placeholder="Enter your new password (min 8 characters)"
                    className="mt-2"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="confirmPassword">
                    Confirm New Password <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm your new password"
                    className="mt-2"
                    required
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-[#2c3e7d] hover:bg-[#1e2a5a]"
                  >
                    {isSubmitting ? (
                      'Changing Password...'
                    ) : (
                      <>
                        <Lock size={16} className="mr-2" />
                        Change Password
                      </>
                    )}
                  </Button>
                  {mustChange ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { logout(); navigate('/login'); }}
                    >
                      Sign Out
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate('/profile')}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;